import React, { useEffect, useState } from 'react';
import { confirmAction } from '../../utils/confirm';
import {
    BatteryType,
    fetchBatteryTypes, createBatteryType, updateBatteryType, deleteBatteryType,
} from '../../services/batteryTypesApi';

const EMPTY_FORM = {
    name: '', label: '', is_coin_cell: true,
    voltage_max_mv: 3200, voltage_min_mv: 2600,
    curve_points_raw: '[[2600,0],[2700,10],[2800,20],[2900,40],[3000,60],[3100,80],[3200,100]]',
};

type FormState = typeof EMPTY_FORM;

function curveToRaw(pts: Array<[number, number]>): string {
    return JSON.stringify(pts);
}
function parseRaw(raw: string): Array<[number, number]> | null {
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        for (const p of parsed) {
            if (!Array.isArray(p) || p.length !== 2 || typeof p[0] !== 'number' || typeof p[1] !== 'number') return null;
        }
        return parsed;
    } catch { return null; }
}

const BatteryTypesPanel: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [types, setTypes] = useState<BatteryType[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [showAdd, setShowAdd] = useState(false);
    const [addForm, setAddForm] = useState<FormState>(EMPTY_FORM);
    const [addSaving, setAddSaving] = useState(false);

    const [editingId, setEditingId] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
    const [editSaving, setEditSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            setTypes(await fetchBatteryTypes());
            setError(null);
        } catch (e: any) {
            setError(e.message || 'Kunde inte hämta batterityper');
        } finally { setLoading(false); }
    };

    useEffect(() => { if (open) load(); }, [open]);

    const handleAdd = async () => {
        const curve = parseRaw(addForm.curve_points_raw);
        if (!curve) { setError('Kurvan är felskriven'); return; }
        setAddSaving(true);
        try {
            await createBatteryType({
                name: addForm.name.trim(), label: addForm.label.trim(),
                is_coin_cell: addForm.is_coin_cell,
                voltage_max_mv: addForm.voltage_max_mv,
                voltage_min_mv: addForm.voltage_min_mv,
                curve_points: curve,
            });
            setAddForm(EMPTY_FORM); setShowAdd(false);
            await load();
        } catch (e: any) { setError(e.message || 'Kunde inte skapa batteritypen'); }
        finally { setAddSaving(false); }
    };

    const startEdit = (t: BatteryType) => {
        setEditingId(t.id);
        setEditForm({
            name: t.name, label: t.label, is_coin_cell: t.is_coin_cell,
            voltage_max_mv: t.voltage_max_mv, voltage_min_mv: t.voltage_min_mv,
            curve_points_raw: curveToRaw(t.curve_points),
        });
    };

    const handleEditSave = async () => {
        if (editingId === null) return;
        const curve = parseRaw(editForm.curve_points_raw);
        if (!curve) { setError('Kurvan är felskriven'); return; }
        setEditSaving(true);
        try {
            await updateBatteryType(editingId, {
                name: editForm.name.trim(), label: editForm.label.trim(),
                is_coin_cell: editForm.is_coin_cell,
                voltage_max_mv: editForm.voltage_max_mv,
                voltage_min_mv: editForm.voltage_min_mv,
                curve_points: curve,
            });
            setEditingId(null);
            await load();
        } catch (e: any) { setError(e.message || 'Kunde inte spara batterityp'); }
        finally { setEditSaving(false); }
    };

    const handleDelete = async (t: BatteryType) => {
        if (!confirmAction(`Ta bort batteritypen "${t.name}"?`)) return;
        try {
            await deleteBatteryType(t.id);
            await load();
        } catch (e: any) { setError(e.message || 'Kunde inte ta bort batteritypen'); }
    };

    const inp: React.CSSProperties = { padding: '5px 8px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px' };
    const FormRow: React.FC<{ form: FormState; setForm: (fn: (f: FormState) => FormState) => void }> = ({ form, setForm }) => (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', alignItems: 'end' }}>
            <div>
                <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>Namn</label>
                <input style={{ ...inp, width: '100%' }} value={form.name} placeholder="CR2032"
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
                <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>Visningsnamn</label>
                <input style={{ ...inp, width: '100%' }} value={form.label} placeholder="CR2032 (knappcell)"
                    onChange={e => setForm(f => ({ ...f, label: e.target.value }))} />
            </div>
            <div>
                <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>Högsta mV (nytt batteri)</label>
                <input type="number" style={{ ...inp, width: '100%' }} value={form.voltage_max_mv}
                    onChange={e => setForm(f => ({ ...f, voltage_max_mv: Number(e.target.value) }))} />
            </div>
            <div>
                <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>Lägsta mV (slut)</label>
                <input type="number" style={{ ...inp, width: '100%' }} value={form.voltage_min_mv}
                    onChange={e => setForm(f => ({ ...f, voltage_min_mv: Number(e.target.value) }))} />
            </div>
            <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', paddingTop: '16px' }}>
                    <input type="checkbox" checked={form.is_coin_cell}
                        onChange={e => setForm(f => ({ ...f, is_coin_cell: e.target.checked }))} />
                    Knappcell
                </label>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '3px' }}>
                    Kurvpunkter <span style={{ color: '#888', fontWeight: 'normal' }}>([[mV, procent], ...])</span>
                </label>
                <textarea style={{ ...inp, width: '100%', fontFamily: 'monospace', fontSize: '12px', resize: 'vertical', minHeight: '56px' }}
                    value={form.curve_points_raw}
                    onChange={e => setForm(f => ({ ...f, curve_points_raw: e.target.value }))} />
                {parseRaw(form.curve_points_raw) === null && (
                    <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>Felskrivet — det ska se ut så här: [[mV, procent], ...]</div>
                )}
            </div>
        </div>
    );

    return (
        <div style={{ marginBottom: '20px', border: '1px solid #dee2e6', borderRadius: '6px', overflow: 'hidden' }}>
            <button type="button" onClick={() => setOpen(o => !o)}
                style={{ width: '100%', background: '#e8f4fd', border: 'none', padding: '10px 14px', cursor: 'pointer', textAlign: 'left', fontWeight: 'bold', fontSize: '13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>🔋 Batterityper</span>
                <span>{open ? '▲' : '▼'}</span>
            </button>
            {open && (
                <div style={{ padding: '14px 16px', background: '#f8fcff' }}>
                    {error && (
                        <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24', padding: '8px 12px', borderRadius: '4px', marginBottom: '10px', fontSize: '13px' }}>
                            {error} <button onClick={() => setError(null)} style={{ marginLeft: '8px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
                        </div>
                    )}

                    {loading ? (
                        <div style={{ color: '#888', fontSize: '13px' }}>Hämtar…</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ background: '#e9ecef' }}>
                                    {['Namn', 'Visningsnamn', 'Knappcell', 'Högsta mV', 'Lägsta mV', 'Kurvpunkter', ''].map(h => (
                                        <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 'bold', borderBottom: '1px solid #dee2e6' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {types.map(t => editingId === t.id ? (
                                    <tr key={t.id} style={{ background: '#fff9e6' }}>
                                        <td colSpan={7} style={{ padding: '10px' }}>
                                            <FormRow form={editForm} setForm={setEditForm as any} />
                                            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                                                <button onClick={handleEditSave} disabled={editSaving}
                                                    style={{ padding: '5px 14px', fontSize: '13px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                                                    {editSaving ? 'Sparar…' : 'Spara'}
                                                </button>
                                                <button onClick={() => setEditingId(null)}
                                                    style={{ padding: '5px 14px', fontSize: '13px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                    Avbryt
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={t.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <td style={{ padding: '6px 10px', fontFamily: 'monospace' }}>{t.name}</td>
                                        <td style={{ padding: '6px 10px' }}>{t.label}</td>
                                        <td style={{ padding: '6px 10px', textAlign: 'center' }}>{t.is_coin_cell ? '✓' : ''}</td>
                                        <td style={{ padding: '6px 10px' }}>{t.voltage_max_mv}</td>
                                        <td style={{ padding: '6px 10px' }}>{t.voltage_min_mv}</td>
                                        <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontSize: '11px', color: '#555' }}>
                                            {t.curve_points.length} punkter
                                        </td>
                                        <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>
                                            <button onClick={() => startEdit(t)}
                                                style={{ padding: '3px 10px', fontSize: '12px', marginRight: '6px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                Ändra
                                            </button>
                                            <button onClick={() => handleDelete(t)}
                                                style={{ padding: '3px 10px', fontSize: '12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                                Ta bort
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Add form */}
                    {showAdd ? (
                        <div style={{ marginTop: '14px', padding: '12px', background: '#fff', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '10px' }}>Ny batterityp</div>
                            <FormRow form={addForm} setForm={setAddForm as any} />
                            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                                <button onClick={handleAdd} disabled={addSaving}
                                    style={{ padding: '5px 14px', fontSize: '13px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                                    {addSaving ? 'Sparar…' : 'Lägg till'}
                                </button>
                                <button onClick={() => setShowAdd(false)}
                                    style={{ padding: '5px 14px', fontSize: '13px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                                    Avbryt
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button onClick={() => setShowAdd(true)}
                            style={{ marginTop: '12px', padding: '6px 14px', fontSize: '13px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                            + Lägg till batterityp
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default BatteryTypesPanel;
